// M12.11 — per-page document.title + meta description.
//
// SPA caveat: OG / Twitter scrapers only see the static `<meta>` baked
// into index.html (they don't execute our React). This hook is for the
// in-browser experience — tab labels, bookmark names, the user's
// history — where React mutations DO reach the user. Per-page OG
// imagery would need SSR or a prerender-route setup, which is out of
// scope for M12.11.

import { useEffect } from "react";

export interface PageMetaOptions {
  /** Full tab title. Convention: "{Page} · Traffic Snapshot". */
  title: string;
  /** Optional description override. When omitted, the index.html
   *  baseline description stays in place. */
  description?: string;
  /** Optional robots override, written into the one existing robots tag
   *  and restored to the index.html baseline on unmount. */
  robots?: string;
}

function setMetaContent(name: string, content: string): void {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", name);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

// Baselines mirror index.html. They are hardcoded, not read from the DOM,
// because the worker rewrites /sample's head, so a visit that starts there
// would otherwise capture the sample's values as the baseline.
const BASELINE_DESCRIPTION =
  "Connect GA4 and get a social-first traffic report in about a minute, plus a UTM Health score that flags broken attribution. Free, from Sked Social.";

const BASELINE_ROBOTS = "index,follow";

export function usePageMeta({ title, description, robots }: PageMetaOptions): void {
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = title;
    setMetaContent("description", description ?? BASELINE_DESCRIPTION);
  }, [title, description]);

  useEffect(() => {
    if (typeof document === "undefined" || !robots) return;
    setMetaContent("robots", robots);
    return () => setMetaContent("robots", BASELINE_ROBOTS);
  }, [robots]);
}
